//@flow

import {useState, useEffect, useCallback} from 'react';
import {
  listProductOptionGroupsService,
  createProductOptionGroupService,
  updateProductOptionGroupService,
  deleteProductOptionGroupService,
  listProductOptionGroupOptionsService,
} from '../promises';
import {uploadImageService} from '../../file-storage/promises';
import {useToastActions} from '../../toast-alert/utils';

import type {
  StoreProductOptionGroup,
  StoreProductOption,
  CreateStoreProductOptionGroupRequest,
  UpdateStoreProductOptionGroupRequest,
} from '../../../entities/product-option';
import type {PaginationRequest, Pagination} from '../../../entities/pagination';
import {useIsFeatureInTier} from '../../../utils/component';

const initialParams = {page: 0, size: 10};

export function useProductOptionGroups() {
  const [items, setItems] = useState<StoreProductOptionGroup[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const {showToast} = useToastActions();

  const fetchData = useCallback(
    async (
      params: PaginationRequest = initialParams,
      append: boolean = false
    ) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setItems([]);
      }

      try {
        const response = await listProductOptionGroupsService(params);

        if (append) {
          setItems(prev => [...prev, ...response.data]);
        } else {
          setItems(response.data);
        }

        setPagination(response.pagination);
      } catch (err) {
        showToast({
          type: 'danger',
          title: 'Error',
          message: 'No se pudieron cargar los grupos de opciones',
        });
        console.error(
          'Error fetching product option groups:',
          err.message || err
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [showToast]
  );

  const loadMore = useCallback(() => {
    if (pagination && pagination.page * pagination.size < pagination.total) {
      const nextPage = pagination.page + 1;
      fetchData({...initialParams, page: nextPage}, true);
    }
  }, [pagination, fetchData]);

  const refetch = useCallback(() => {
    fetchData(initialParams, false);
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const hasMore = pagination
    ? (pagination.page + 1) * pagination.size < pagination.total
    : false;

  return {
    items,
    pagination,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refetch,
  };
}

export function useProductOptionGroupActions() {
  const [loading, setLoading] = useState<boolean>(false);
  const {showToast} = useToastActions();

  const hasProductOptionsFeature = useIsFeatureInTier('PRODUCT_OPTIONS');

  const processOptionsWithImages = useCallback(async (options: any[]) => {
    return Promise.all(
      options.map(async (option, index) => {
        let imageId = option.imageId;

        if (option.imageFile) {
          try {
            const uploadResponse = await uploadImageService(
              option.imageFile,
              'im/product-options'
            );
            imageId = uploadResponse[0]?.id;
          } catch (err) {
            console.error('Error uploading image:', err);
          }
        }

        return {
          name: option.name,
          color: option.color || undefined,
          number: index,
          enabled: option.enabled !== undefined ? option.enabled : true,
          price: option.price ? option.price.toString() : '0',
          imageId: imageId || undefined,
          storeProductOptionId: option.storeProductOptionId || undefined,
        };
      })
    );
  }, []);

  const validateFeature = useCallback(() => {
    if (!hasProductOptionsFeature) {
      showToast({
        type: 'warning',
        title: 'Sin acceso',
        message: 'No tienes acceso a esta funcionalidad',
      });
      return false;
    }
    return true;
  }, [hasProductOptionsFeature, showToast]);

  const createGroup = useCallback(
    async (
      request: CreateStoreProductOptionGroupRequest
    ): Promise<StoreProductOptionGroup | null> => {
      if (!validateFeature()) return null;
      setLoading(true);
      try {
        const processedOptions = await processOptionsWithImages(
          request.options || []
        );

        const processedRequest = {
          ...request,
          options: processedOptions,
        };

        const response = await createProductOptionGroupService(
          processedRequest
        );
        showToast({
          type: 'success',
          title: 'Éxito',
          message: 'Grupo de opciones creado exitosamente',
        });
        return response;
      } catch (err) {
        showToast({
          type: 'danger',
          title: 'Error',
          message: 'No se pudo crear el grupo de opciones',
        });
        console.error(
          'Error creating product option group:',
          err.message || err
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [validateFeature, processOptionsWithImages, showToast]
  );

  const updateGroup = useCallback(
    async (
      storeProductOptionGroupId: string,
      request: UpdateStoreProductOptionGroupRequest
    ): Promise<StoreProductOptionGroup | null> => {
      if (!validateFeature()) return null;
      setLoading(true);
      try {
        const processedOptions = await processOptionsWithImages(
          request.options || []
        );

        const processedRequest = {
          ...request,
          options: processedOptions,
        };

        const response = await updateProductOptionGroupService(
          storeProductOptionGroupId,
          processedRequest
        );
        showToast({
          type: 'success',
          title: 'Éxito',
          message: 'Grupo de opciones actualizado exitosamente',
        });
        return response;
      } catch (err) {
        showToast({
          type: 'danger',
          title: 'Error',
          message: 'No se pudo actualizar el grupo de opciones',
        });
        console.error(
          'Error updating product option group:',
          err.message || err
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [validateFeature, showToast, processOptionsWithImages]
  );

  const deleteGroup = useCallback(
    async (storeProductOptionGroupId: string): Promise<boolean> => {
      if (!validateFeature()) return false;
      setLoading(true);
      try {
        await deleteProductOptionGroupService(storeProductOptionGroupId);
        showToast({
          type: 'success',
          title: 'Éxito',
          message: 'Grupo de opciones eliminado exitosamente',
        });
        return true;
      } catch (err) {
        showToast({
          type: 'danger',
          title: 'Error',
          message: 'No se pudo eliminar el grupo de opciones',
        });
        console.error(
          'Error deleting product option group:',
          err.message || err
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [showToast, validateFeature]
  );

  return {
    createGroup,
    updateGroup,
    deleteGroup,
    loading,
  };
}

export function useProductOptionGroupOptions(
  storeProductOptionGroupId?: string
) {
  const [items, setItems] = useState<StoreProductOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const {showToast} = useToastActions();

  const fetchData = useCallback(
    async (append: boolean = false) => {
      if (!storeProductOptionGroupId) {
        setItems([]);
        return;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setItems([]);
      }

      try {
        const response = await listProductOptionGroupOptionsService(
          storeProductOptionGroupId
        );

        if (append) {
          setItems(prev => [...prev, ...response.data]);
        } else {
          setItems(response.data);
        }
      } catch (err) {
        showToast({
          type: 'danger',
          title: 'Error',
          message: 'No se pudieron cargar las opciones del grupo',
        });
        console.error(
          'Error fetching product option group options:',
          err.message || err
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [storeProductOptionGroupId, showToast]
  );

  const loadMore = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData(false);
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    items,
    loading,
    loadingMore,
    loadMore,
    refetch,
  };
}
